import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button, ButtonProps } from '@/components/ui/button';
import { popModal } from '.';

const selectedViewModel = ({
  modalApi,
  initialShow,
}: {
  modalApi: ConfirmationModalApi;
  initialShow: boolean;
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [show, setShow] = useState<boolean>(initialShow);

  const closeModal = () => {
    popModal('ConfirmationModal');
    setShow(false);
    modalApi.onClose?.();
  };

  const cancelModal = () => {
    popModal('ConfirmationModal');
    setShow(false);
    modalApi.onCancel?.();
  };

  return {
    api: {
      start: () => {
        setLoading(true);
      },
      success: () => {
        setLoading(false);
        setShow(false);
      },
      failed: () => {
        setLoading(false);
      },
      submit: (viewModel: any) => () => {
        modalApi.onConfirm(viewModel);
      },
      close: closeModal,
      cancel: cancelModal,
    },
    data: {
      loading,
      show,
    },
  };
};
export type ConfirmationModalViewModel = ReturnType<typeof selectedViewModel>;

export const useConfirmModalViewModel = ({
  isLoading,
  isSuccess,
  isError,
}: {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
}) => {
  const [modalViewModel, setModalViewModel] =
    useState<ConfirmationModalViewModel>();

  // Handle loading
  useEffect(() => {
    if (!modalViewModel) {
      return;
    }

    if (isLoading) {
      modalViewModel.api.start();
    } else if (isSuccess) {
      modalViewModel.api.success();
    } else if (isError) {
      modalViewModel.api.failed();
    }
  }, [modalViewModel, isLoading, isSuccess, isError]);

  return {
    setModalViewModel,
  };
};

export type ConfirmationModalApi = {
  onConfirm: (viewModel: ConfirmationModalViewModel) => void;
  onClose?: () => void;
  onCancel?: () => void;
};

export type ConfirmationModalProps = {
  show?: boolean;
  title: string;
  description: string;
  btnLabel?: string;
  btnVariant?: ButtonProps['variant'];
} & ConfirmationModalApi;

export const ConfirmationModal = forwardRef(
  (
    {
      show = true,
      title,
      description,
      btnLabel = 'btn.confirm',
      btnVariant = 'default',
      onConfirm,
      onClose,
      onCancel,
    }: ConfirmationModalProps,
    ref,
  ) => {
    const { t } = useTranslation();

    const viewModel = selectedViewModel({
      initialShow: show,
      modalApi: {
        onConfirm,
        onClose,
        onCancel,
      },
    });
    useImperativeHandle(ref, () => {
      return viewModel;
    }, []);

    return (
      <AlertDialog
        open={viewModel.data.show}
        onOpenChange={(open) => {
          if (!open) viewModel.api.close();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t(title)}</AlertDialogTitle>
            <AlertDialogDescription
              dangerouslySetInnerHTML={{ __html: t(description) }}
            />
          </AlertDialogHeader>

          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={viewModel.api.cancel}
            >
              {t('btn.close')}
            </Button>

            <Button
              variant={btnVariant}
              onClick={viewModel.api.submit(viewModel)}
              loading={viewModel.data.loading}
            >
              {t(btnLabel)}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  },
);
